import { useState } from 'react';
import type { Lang } from '../page';

interface FooterProps { lang: Lang; }

type LM = Record<Lang, string>;

const visitLabel: LM = { en: 'Visit Our Office', zh: '公司地址', th: 'ที่อยู่สำนักงาน', vi: 'Địa Chỉ Văn Phòng', ms: 'Alamat Pejabat' };
const callLabel: LM = { en: 'Call Us', zh: '电话', th: 'โทรหาเรา', vi: 'Gọi Cho Chúng Tôi', ms: 'Hubungi Kami' };
const emailLabel: LM = { en: 'Email Us', zh: '邮箱', th: 'อีเมลหาเรา', vi: 'Gửi Email', ms: 'E-mel Kami' };
const tagline: LM = {
  en: 'High-Performance Materials\nLeading Future Technology',
  zh: '高性能材料\n引领未来科技',
  th: 'วัสดุประสิทธิภาพสูง\nนำหน้าเทคโนโลยีอนาคต',
  vi: 'Vật Liệu Hiệu Suất Cao\nDẫn Đầu Công Nghệ Tương Lai',
  ms: 'Bahan Berprestasi Tinggi\nMemimpin Teknologi Masa Depan',
};
const copyright: LM = {
  en: '© 2025 Polyimide Film. All rights reserved.',
  zh: '© 2025 Polyimide Film. 版权所有。',
  th: '© 2025 Polyimide Film. สงวนลิขสิทธิ์.',
  vi: '© 2025 Polyimide Film. Bảo lưu mọi quyền.',
  ms: '© 2025 Polyimide Film. Hak cipta terpelihara.',
};
const privacyLabel: LM = { en: 'Privacy Policy', zh: '隐私政策', th: 'นโยบายความเป็นส่วนตัว', vi: 'Chính Sách Bảo Mật', ms: 'Dasar Privasi' };
const websiteBuilder: LM = { en: 'Website Builder', zh: '网站构建', th: 'ตัวสร้างเว็บไซต์', vi: 'Công Cụ Tạo Web', ms: 'Pembina Laman Web' };
const wechatId: LM = { en: 'WeChat ID', zh: '微信号', th: 'WeChat ID', vi: 'WeChat ID', ms: 'ID WeChat' };
const wechatCopy: LM = {
  en: 'Click to copy and add on WeChat',
  zh: '复制微信号添加好友',
  th: 'คลิกเพื่อคัดลอกและเพิ่มบน WeChat',
  vi: 'Nhấp để sao chép và thêm trên WeChat',
  ms: 'Klik untuk salin dan tambah di WeChat',
};

const footerSections = [
  {
    title: { en: 'Products', zh: '产品中心', th: 'ผลิตภัณฑ์', vi: 'Sản Phẩm', ms: 'Produk' } as LM,
    links: [
      { name: { en: 'Standard Film', zh: '标准薄膜', th: 'ฟิล์มมาตรฐาน', vi: 'Màng Tiêu Chuẩn', ms: 'Filem Standard' } as LM, href: '#' },
      { name: { en: 'High-Performance Film', zh: '高性能薄膜', th: 'ฟิล์มประสิทธิภาพสูง', vi: 'Màng Hiệu Suất Cao', ms: 'Filem Berprestasi Tinggi' } as LM, href: '#' },
      { name: { en: 'Custom Products', zh: '定制产品', th: 'ผลิตภัณฑ์ตามสั่ง', vi: 'Sản Phẩm Tùy Chỉnh', ms: 'Produk Tersuai' } as LM, href: '#' },
    ],
  },
  {
    title: { en: 'Technical Support', zh: '技术支持', th: 'การสนับสนุนทางเทคนิค', vi: 'Hỗ Trợ Kỹ Thuật', ms: 'Sokongan Teknikal' } as LM,
    links: [
      { name: { en: 'Documentation', zh: '技术文档', th: 'เอกสารทางเทคนิค', vi: 'Tài Liệu Kỹ Thuật', ms: 'Dokumentasi' } as LM, href: '#' },
      { name: { en: 'Case Studies', zh: '案例研究', th: 'กรณีศึกษา', vi: 'Nghiên Cứu Trường Hợp', ms: 'Kajian Kes' } as LM, href: '#' },
      { name: { en: 'FAQ', zh: '常见问题', th: 'คำถามพบบ่อย', vi: 'FAQ', ms: 'Soalan Lazim' } as LM, href: '#faq' },
    ],
  },
  {
    title: { en: 'About Us', zh: '关于我们', th: 'เกี่ยวกับเรา', vi: 'Về Chúng Tôi', ms: 'Tentang Kami' } as LM,
    links: [
      { name: { en: 'Company Profile', zh: '公司简介', th: 'โปรไฟล์บริษัท', vi: 'Hồ Sơ Công Ty', ms: 'Profil Syarikat' } as LM, href: '#' },
      { name: { en: 'Certifications', zh: '资质认证', th: 'การรับรอง', vi: 'Chứng Nhận', ms: 'Sijil' } as LM, href: '#certifications' },
      { name: { en: 'Contact Us', zh: '联系我们', th: 'ติดต่อเรา', vi: 'Liên Hệ', ms: 'Hubungi Kami' } as LM, href: '#' },
    ],
  },
  {
    title: { en: 'Contact', zh: '联系方式', th: 'ติดต่อ', vi: 'Liên Hệ', ms: 'Kenalan' } as LM,
    links: [
      { name: { en: 'Online Support', zh: '在线支持', th: 'สนับสนุนออนไลน์', vi: 'Hỗ Trợ Trực Tuyến', ms: 'Sokongan Dalam Talian' } as LM, href: '#' },
      { name: { en: 'Technical Consultation', zh: '技术咨询', th: 'ปรึกษาทางเทคนิค', vi: 'Tư Vấn Kỹ Thuật', ms: 'Perundingan Teknikal' } as LM, href: '#' },
      { name: { en: 'Business Cooperation', zh: '商务合作', th: 'ความร่วมมือทางธุรกิจ', vi: 'Hợp Tác Kinh Doanh', ms: 'Kerjasama Perniagaan' } as LM, href: '#' },
    ],
  },
];

const socialMedia = [
  { icon: 'ri-wechat-line', href: '#', label: 'WeChat', id: 'wechat' },
  { icon: 'ri-whatsapp-line', href: 'https://wa.me/6581586399', label: 'WhatsApp' },
  { icon: 'ri-linkedin-line', href: '#', label: 'LinkedIn' },
  { icon: 'ri-mail-line', href: 'mailto:info@veyang.com', label: 'Email' },
  { icon: 'ri-phone-line', href: 'tel:+6581586399', label: 'Phone' },
];

export default function Footer({ lang }: FooterProps) {
  const [showWechat, setShowWechat] = useState(false);

  return (
    <footer className="bg-gradient-to-br from-teal-900 via-teal-800 to-teal-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-12 mb-16">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center space-x-2">
              <img src="https://public.readdy.ai/ai/img_res/2bd46c8d-4232-4ca7-9f70-77346d902090.png" alt="Logo" className="h-10 w-auto object-contain" />
              <span className="text-2xl font-bold">Polyimide Film</span>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">{visitLabel[lang]}</div>
                  <div className="text-white/80 text-sm">8B Admiralty St, #08-07<br />Singapore 759969</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-phone-line text-xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">{callLabel[lang]}</div>
                  <a href="tel:+6581586399" className="text-white/80 text-sm hover:text-white transition-colors">+65 8158 6399</a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-whatsapp-line text-xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">WhatsApp</div>
                  <a href="https://wa.me/6581586399" target="_blank" rel="noopener noreferrer" className="text-white/80 text-sm hover:text-white transition-colors">+65 8158 6399</a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-mail-line text-xl text-amber-500 mt-1"></i>
                <div>
                  <div className="font-semibold mb-1">{emailLabel[lang]}</div>
                  <a href="mailto:info@veyang.com" className="text-white/80 text-sm hover:text-white transition-colors">info@veyang.com</a>
                </div>
              </div>
            </div>

            <p className="text-2xl font-serif leading-tight pt-4 whitespace-pre-line">{tagline[lang]}</p>
          </div>

          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-8">
            {footerSections.map((section, index) => (
              <div key={index}>
                <h3 className="font-bold text-lg mb-4">{section.title[lang]}</h3>
                <ul className="space-y-2">
                  {section.links.map((link, li) => (
                    <li key={li}>
                      <a href={link.href} className="text-white/80 hover:text-white transition-colors text-sm">{link.name[lang]}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-sm text-white/60">{copyright[lang]}</div>

            <div className="flex items-center space-x-4 relative">
              {socialMedia.map((social, index) => (
                social.id === 'wechat' ? (
                  <button
                    key={index}
                    onClick={() => setShowWechat(!showWechat)}
                    className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                    aria-label={social.label}
                  >
                    <i className={`${social.icon} text-xl`}></i>
                  </button>
                ) : (
                  <a
                    key={index}
                    href={social.href}
                    className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
                    aria-label={social.label}
                  >
                    <i className={`${social.icon} text-xl`}></i>
                  </a>
                )
              ))}
              {showWechat && (
                <div className="absolute bottom-full mb-2 left-0 bg-white text-gray-900 rounded-lg shadow-2xl p-4 min-w-[200px] z-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{wechatId[lang]}</span>
                    <button onClick={() => setShowWechat(false)} className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded transition-colors">
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 rounded px-3 py-2">
                    <i className="ri-wechat-fill text-green-500 text-xl"></i>
                    <span className="font-mono text-sm">James_Bonwin</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">{wechatCopy[lang]}</div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-4 text-sm text-white/60">
              <a href="#" className="hover:text-white transition-colors">{privacyLabel[lang]}</a>
              <span>|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{websiteBuilder[lang]}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
